import { useEffect, useState, type ReactNode } from 'react';
import {
  createRootRouteWithContext,
  HeadContent,
  Link,
  Outlet,
  ScriptOnce,
  Scripts,
  useRouterState,
} from '@tanstack/react-router';
import { Compass } from '../components/icons';

import { OffsideBanner } from '../components/banner/offside-banner';
import { useMe } from '../features/auth/use-me';
import { IdentitySheetProvider, useIdentitySheet } from '../features/auth/identity-sheet';
import { useAuth } from '../lib/auth';
import { Footer } from '../components/layout/footer';
import { Header } from '../components/layout/header';
import { MobileNav } from '../components/layout/mobile-nav';
import { OverscrollReveal } from '../components/layout/overscroll-reveal';
import { TopNavActiveProvider } from '../components/layout/top-nav-active';
import { bannersQuery, BannersProvider, useBannersQuery } from '../features/banners/banners';
import type { RouterContext } from '../router';
import { PageContainer } from '../components/layout/page-container';
import { EmptyState } from '../components/ui/empty-state';
import { PILL_TAB, PILL_TAB_ON } from '../components/ui/pill-tab';
import { cn } from '../lib/utils';
import { SnackbarProvider } from '../components/ui/magic-snackbar';
import { trackPageView } from '../lib/analytics';
import { SITE_NAME } from '../lib/site';

/**
 * FOUC 방지 — React 가 뜨기 전에 OS 다크모드 선호를 <html> 에 즉시 적용한다(토큰 자동 swap).
 * ScriptOnce 는 서버 HTML 에만 들어가고 실행 직후 스스로 지워진다. <html> 의 class·style 을
 * 바꾸므로 <html> 에 suppressHydrationWarning 을 둔다.
 */
const THEME_BOOT = `(function(){try{if(window.matchMedia('(prefers-color-scheme: dark)').matches){document.documentElement.classList.add('dark');document.documentElement.style.colorScheme='dark'}}catch(e){}})()`;

/**
 * 폰트 CSS 는 렌더를 막지 않게 받는다(media="print" 로 받고 → 도착하면 media="all"). 그동안은
 * 대체 글꼴로 먼저 그린다(display=swap). Lighthouse 「렌더 차단 리소스」 3.7초의 대부분이 이 둘이었다.
 *
 * `onload` 속성은 React 가 서버 HTML 에 쓰지 않는다. 그래서 링크 뒤의 스크립트가 켠다.
 * **이미 도착했으면 바로 켠다(`l.sheet`).** 이 스크립트는 앞의 본 CSS(`/assets/main-*.css`)가 올 때까지
 * 실행이 멈춘다 — 첫 방문엔 그사이 폰트 CSS 의 load 가 먼저 지나가 리스너가 놓쳤고, 새로고침 전까지
 * 워드마크(기랑해랑)가 대체 글꼴로 남았다. 예전 preload 방식에는 「이미 왔는가」를 알 길이 없었다.
 *
 * media 만 바뀌는 속성 차이라 hydration 이 되돌리지 않는다. 이 링크는 head() 가 아니라 셸에 둔다 —
 * 라우터가 이동 때 다시 그리면 media 가 print 로 돌아간다.
 */
const FONT_CSS = [
  'https://fonts.googleapis.com/css2?family=Kirang+Haerang&family=Noto+Serif+KR:wght@400;600;700&display=swap',
  'https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/variable/pretendardvariable-dynamic-subset.min.css',
];
const FONT_SWAP = `document.querySelectorAll('link[data-font-css]').forEach(function(l){if(l.sheet)l.media='all';else l.addEventListener('load',function(){l.media='all'},{once:true})})`;

/**
 * AdSense 는 첫 그리기 뒤에 붙인다 — 250KB 짜리 스크립트와 iframe 이 LCP 앞을 막지 않게.
 * 계정 확인은 google-adsense-account 메타로 된다. 광고(Auto ads)는 늦게 붙어도 그대로 나온다.
 * <head> 끝에 붙는 노드라 hydration 과 부딪히지 않는다(React 18 은 head · body 끝의 여분 노드를 둔다).
 */
const ADSENSE_LATER = `(function(){var load=function(){var s=document.createElement('script');s.async=true;s.crossOrigin='anonymous';s.src='https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-7041169429149149';document.head.appendChild(s)};var later=function(){if('requestIdleCallback' in window)window.requestIdleCallback(load,{timeout:3000});else setTimeout(load,1)};if(document.readyState==='complete')later();else window.addEventListener('load',later)})()`;

/**
 * Root layout shell — ported from the Seasonly design system (fruit/react).
 * Sticky header (spotlight menu) + content column + footer + mobile bottom nav,
 * all on the `background` token surface.
 */
export const Route = createRootRouteWithContext<RouterContext>()({
  // CSR E2E 전용 SPA 모드. 그 spec 들은 브라우저의 page.route 로 API 를 흉내 내는데, 서버 loader 에는
  // 그 흉내가 닿지 않는다. 루트가 ssr: false 면 서버는 셸만 그리고 모든 loader 가 브라우저에서 돈다.
  // 환경 변수가 없으면(배포 · 개발) undefined — 라우트마다 기본값(SSR)이다.
  ssr: import.meta.env.VITE_E2E_SPA === '1' ? false : undefined,
  // 배너 두 장은 모든 화면(우하단 고정)에 든다. 실패해도 기본 그림이 있어 화면을 안 막는다.
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(bannersQuery).catch(() => undefined);
  },
  head: () => ({
    meta: [
      { charSet: 'UTF-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1.0' },
      { name: 'color-scheme', content: 'light dark' },
      { name: 'naver-site-verification', content: '0fb2f03ac9db4070ce57f45972d23939d7ae00f8' },
      { name: 'google-adsense-account', content: 'ca-pub-7041169429149149' },
      { title: SITE_NAME },
    ],
    links: [
      { rel: 'icon', type: 'image/svg+xml', href: '/logo.svg' },
      // 폰트 — Kirang Haerang(머리 · 바닥 워드마크) · Noto Serif KR(아티클 종이) · Pretendard(본문).
      { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
      { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
      { rel: 'preconnect', href: 'https://cdn.jsdelivr.net', crossOrigin: 'anonymous' },
    ],
  }),
  shellComponent: RootDocument,
  component: RootLayout,
  // 모르는 주소(옛 /community · /me · /grants 등 — 리다이렉트하지 않는다, 2026-09-24 형 결정)는 이 화면. 껍데기만 보이면 안 된다.
  notFoundComponent: () => (
    <PageContainer>
      <EmptyState
        icon={<Compass />}
        title="페이지가 없다."
        description="주소가 바뀌었거나 지워졌다."
        action={<Link to="/news" className={cn(PILL_TAB, PILL_TAB_ON)}>지원사업으로</Link>}
      />
    </PageContainer>
  ),
});

/**
 * 문서 전체 — 서버가 이 트리를 HTML 로 그리고 브라우저가 hydrateRoot(document) 로 이어받는다.
 * 라우트별 제목·메타·JSON-LD 는 각 라우트의 head() 가 <HeadContent /> 로 들어온다.
 * <Scripts /> 는 라우터 상태(Query 캐시 포함)와 클라이언트 진입 스크립트를 넣는다.
 */
function RootDocument({ children }: { children: ReactNode }) {
  return (
    <html lang="ko" suppressHydrationWarning>
      <head>
        <ScriptOnce>{THEME_BOOT}</ScriptOnce>
        <HeadContent />
        {FONT_CSS.map((href) => (
          <link key={href} rel="stylesheet" media="print" href={href} data-font-css="" suppressHydrationWarning />
        ))}
        <ScriptOnce>{FONT_SWAP}</ScriptOnce>
        <noscript
          dangerouslySetInnerHTML={{
            __html: FONT_CSS.map((href) => `<link rel="stylesheet" href="${href}" />`).join(''),
          }}
        />
        <ScriptOnce>{ADSENSE_LATER}</ScriptOnce>
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

/**
 * GA4 page_view — 이동이 끝날 때마다. 서버가 그린 첫 화면은 hydration 뒤 router.load() 를
 * 거치지 않아 onResolved 가 오지 않는다. 그래서 구독 대신 resolvedLocation 을 본다.
 * 문서 제목은 head() 가 바꾼 뒤라 setTimeout 0.
 */
function usePageViews() {
  const href = useRouterState({ select: (s) => s.resolvedLocation?.href });
  useEffect(() => {
    if (!href) return;
    const t = setTimeout(() => trackPageView(href), 0);
    return () => clearTimeout(t);
  }, [href]);
}

/**
 * 비밀번호 재설정 링크를 받아 넘긴다.
 *
 * **Supabase 의 재설정 메일은 Site URL(`zoxo.kr`) 로 돌아온다.** 어느 경로도
 * 아닌 루트로 떨어지고, 토큰은 URL 해시에 실려 온다
 * (`#access_token=…&type=recovery`).
 *
 * 이 앱은 `detectSessionInUrl: false` 라 SDK 가 해시를 자동 소비하지 않고,
 * `/auth/callback` 은 카카오 OAuth 전용(`?code=`)이라 해시를 못 본다. 그래서
 * 링크를 눌러도 그냥 첫 화면이 뜨고 끝났다 — 토큰이 URL 에 남은 채 아무도
 * 읽지 않았다.
 *
 * 여기서 알아채고 `/auth/reset` 으로 넘긴다. **네이티브 이동을 쓴다** —
 * 라우터 이동은 해시를 보존하지 않아 토큰이 사라진다.
 *
 * Site URL 을 바꾸지 않는 이유: 그 값은 카카오 로그인 등 다른 흐름도 쓴다.
 * 받는 쪽을 만드는 편이 부작용이 없다.
 */
function useRecoveryRedirect() {
  useEffect(() => {
    const { hash, pathname } = window.location;
    if (!hash.includes('type=recovery')) return;
    if (pathname === '/auth/reset') return;
    window.location.replace(`/auth/reset${hash}`);
  }, []);
}

/**
 * **끌고 가지 않는다 — 한 줄로 권한다.** 병아리로 시작해 정체성을 아직 안 정했으면 머리 아래에 얇은 안내 한 줄.
 * 강제로 /onboarding 에 보내면 첫 만남부터 옥죄는 느낌이라 뺐다. 닫으면 그 세션 동안 안 뜬다.
 * 투표·댓글·글쓰기 자리의 게이트와 마이페이지의 스테퍼는 그대로다.
 */
const NUDGE_KEY = 'zoxo.onboarding-nudge.dismissed';

function OnboardingNudge() {
  const { isAuthenticated } = useAuth();
  const me = useMe();
  const { open } = useIdentitySheet();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // 저장소는 render 가 아니라 effect 에서 읽는다 — 서버엔 sessionStorage 가 없어 첫 렌더가 달라진다.
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    try {
      if (sessionStorage.getItem(NUDGE_KEY) === '1') setDismissed(true);
    } catch {
      /* 못 읽으면 닫히지 않은 것으로 둔다 */
    }
  }, []);
  // 병아리로 시작해 아직 정체성을 안 정한 사람(visitReason 이 비어 있다)에게만.
  const show = isAuthenticated && me.data !== undefined && me.data.visitReason === null && !dismissed
    && pathname !== '/onboarding' && pathname !== '/mypage';
  if (!show) return null;
  return (
    <div className="mx-auto flex w-full max-w-4xl items-center gap-2 bg-surface px-4 pb-2 text-xs text-text-muted">
      <span aria-hidden>🐣</span>
      <span className="flex-1">
        병아리로 시작했어요.{' '}
        <button type="button" onClick={open} className="font-medium text-text underline underline-offset-2">
          정체성 정하기 →
        </button>
      </span>
      <button
        type="button"
        aria-label="닫기"
        className="rounded px-1 text-text-subtle hover:text-text"
        onClick={() => {
          setDismissed(true);
          try { sessionStorage.setItem(NUDGE_KEY, '1'); } catch { /* 저장 못 해도 이번 화면에선 닫힌다 */ }
        }}
      >
        ×
      </button>
    </div>
  );
}

function RootLayout() {
  useRecoveryRedirect();
  usePageViews();
  // 배너 두 장 — 루트 loader 가 채운 ['banners'] 캐시를 이어받는다. 없으면 기본 그림으로 그리고 받아서 바꾼다.
  const { data: banners } = useBannersQuery();

  return (
    <SnackbarProvider>
    <BannersProvider value={banners}>
      <OverscrollReveal />

      {/* 화면 우하단 고정 배너. 여백이 확보되는 폭(1464px+)에서만 보인다. */}
      <OffsideBanner />

      <TopNavActiveProvider>
        <IdentitySheetProvider>
        <div className="relative flex min-h-screen flex-col bg-background text-text">
          <Header />
          <OnboardingNudge />
          <div className="flex flex-1 flex-col">
            <Outlet />
          </div>
          <Footer />
          <MobileNav />
        </div>
        </IdentitySheetProvider>
      </TopNavActiveProvider>
    </BannersProvider>
    </SnackbarProvider>
  );
}
